/*--------------------------------------------------------------
# Logger Utility - Console wrapper with debug toggle
--------------------------------------------------------------*/

/**
 * Configuration du logger
 * Peut être modifié via window.DEBUG_CONFIG dans la console
 * ou persisté dans localStorage avec la clé 'DEBUG_CONFIG'
 */
const DEFAULT_CONFIG = {
    // Active/désactive tous les logs
    enabled: true,

    // Active/désactive par namespace
    namespaces: {
        configurateur: false,
        api: false,
        directive: false,
        component: false,
        planning: false,
    }
};

/**
 * Charge la configuration depuis localStorage
 */
function loadConfigFromStorage() {
    try {
        const stored = localStorage.getItem('DEBUG_CONFIG');
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Erreur lors du chargement de DEBUG_CONFIG depuis localStorage:', e);
    }
    return null;
}

/**
 * Sauvegarde la configuration dans localStorage
 */
function saveConfigToStorage(config) {
    try {
        localStorage.setItem('DEBUG_CONFIG', JSON.stringify(config));
    } catch (e) {
        console.error('Erreur lors de la sauvegarde de DEBUG_CONFIG dans localStorage:', e);
    }
}

window.clearDebugConfig = () => {
    localStorage.removeItem('DEBUG_CONFIG');
}

window.setDebugConfig = (namespace, value) => {
    if (namespace === 'enabled') {
        window.DEBUG_CONFIG.enabled = value;
        saveConfigToStorage(window.DEBUG_CONFIG);
    } else if (namespace === 'all') {
        for (const key in window.DEBUG_CONFIG.namespaces) {
            window.DEBUG_CONFIG.namespaces[key] = value;
        }
        saveConfigToStorage(window.DEBUG_CONFIG);
    } else {
        window.DEBUG_CONFIG.namespaces[namespace] = value;
        saveConfigToStorage(window.DEBUG_CONFIG);
    }
}

// Configuration globale accessible
// Priorité: localStorage > config existante > DEFAULT_CONFIG
const storedConfig = loadConfigFromStorage();
if (!window.DEBUG_CONFIG) {
    window.DEBUG_CONFIG = DEFAULT_CONFIG;
}
if (storedConfig) {
    window.DEBUG_CONFIG = storedConfig;
}

/**
 * Classe Logger - Wrapper autour de console avec options de debug
 *
 * @example
 * // Activer tous les logs
 * window.DEBUG_CONFIG.enabled = true;
 *
 * // Activer uniquement le configurateur
 * window.DEBUG_CONFIG.namespaces.configurateur = true;
 *
 * // Utilisation
 * const logger = new Logger('configurateur');
 * logger.log('Mon message'); // Affiche si enabled ou namespace configurateur actif
 */
class Logger {
    /**
     * @param {string} namespace - Nom du namespace (configurateur, api, directive, etc.)
     */
    constructor(namespace = 'default') {
        this.namespace = namespace;

        // Initialiser le namespace s'il n'existe pas
        if (window.DEBUG_CONFIG.namespaces[namespace] === undefined) {
            window.DEBUG_CONFIG.namespaces[namespace] = false;
        }
    }

    /**
     * Vérifie si les logs sont activés pour ce namespace
     */
    isEnabled() {
        return window.DEBUG_CONFIG.enabled &&
            window.DEBUG_CONFIG.namespaces[this.namespace] === true;
    }

    /**
     * Formate le préfixe du log avec le namespace
     */
    getPrefix() {
        return `[${this.namespace.toUpperCase()}]`;
    }

    /**
     * Log standard
     */
    log(...args) {
        if (this.isEnabled()) {
            console.log(this.getPrefix(), ...args);
        }
    }

    /**
     * Warning
     */
    warn(...args) {
        if (this.isEnabled()) {
            console.warn(this.getPrefix(), ...args);
        }
    }

    /**
     * Error (toujours affiché même si debug désactivé)
     */
    error(...args) {
        console.error(this.getPrefix(), ...args);
    }

    /**
     * Table
     */
    table(data, columns) {
        if (this.isEnabled()) {
            console.log(this.getPrefix());
            console.table(data, columns);
        }
    }

    /**
     * Group
     */
    group(label) {
        if (this.isEnabled()) {
            console.group(`${this.getPrefix()} ${label}`);
        }
    }

    /**
     * Group collapsed
     */
    groupCollapsed(label) {
        if (this.isEnabled()) {
            console.groupCollapsed(`${this.getPrefix()} ${label}`);
        }
    }

    /**
     * Group end
     */
    groupEnd() {
        if (this.isEnabled()) {
            console.groupEnd();
        }
    }

    /**
     * Info
     */
    info(...args) {
        if (this.isEnabled()) {
            console.info(this.getPrefix(), ...args);
        }
    }

    /**
     * Debug
     */
    debug(...args) {
        if (this.isEnabled()) {
            console.debug(this.getPrefix(), ...args);
        }
    }

    /**
     * Time
     */
    time(label) {
        if (this.isEnabled()) {
            console.time(`${this.getPrefix()} ${label}`);
        }
    }

    /**
     * Time end
     */
    timeEnd(label) {
        if (this.isEnabled()) {
            console.timeEnd(`${this.getPrefix()} ${label}`);
        }
    }

    /**
     * Assert
     */
    assert(condition, ...args) {
        if (this.isEnabled()) {
            console.assert(condition, this.getPrefix(), ...args);
        }
    }
}

/**
 * Helper pour activer rapidement le debug depuis la console
 */
window.enableDebug = (namespace = null) => {
    if (namespace) {
        window.DEBUG_CONFIG.namespaces[namespace] = true;
        console.log(`✅ Debug activé pour: ${namespace}`);
    } else {
        window.DEBUG_CONFIG.enabled = true;
        console.log('✅ Debug activé globalement');
    }
};

window.disableDebug = (namespace = null) => {
    if (namespace) {
        window.DEBUG_CONFIG.namespaces[namespace] = false;
        console.log(`❌ Debug désactivé pour: ${namespace}`);
    } else {
        window.DEBUG_CONFIG.enabled = false;
        console.log('❌ Debug désactivé globalement');
    }
};

window.showDebugConfig = () => {
    console.table({
        'Global': window.DEBUG_CONFIG.enabled,
        ...window.DEBUG_CONFIG.namespaces
    });
};

export default Logger;
